import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ScrollToTop } from '../components/ScrollToTop';
import { 
  Users, Gift, LogOut, Copy, Check, 
  Shield, Trophy, Sparkles, Target, MessageCircle,
  Clock, Phone, Calendar, Home, UserCheck, Award, HelpCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration with icons and colors
const STATUS_CONFIG = {
  'nuevo_lead': { 
    label: 'Registrado', 
    icon: UserCheck, 
    color: 'text-blue-400', 
    bg: 'bg-blue-400/10', 
    border: 'border-blue-400/30',
    description: 'Se ha registrado'
  },
  'registrado': { 
    label: 'Registrado', 
    icon: UserCheck, 
    color: 'text-blue-400', 
    bg: 'bg-blue-400/10', 
    border: 'border-blue-400/30',
    description: 'Se ha registrado'
  },
  'contactado': { 
    label: 'Contactado', 
    icon: Phone, 
    color: 'text-yellow-400', 
    bg: 'bg-yellow-400/10', 
    border: 'border-yellow-400/30',
    description: 'En proceso de contacto'
  },
  'prueba_agendada': { 
    label: 'Visita agendada', 
    icon: Calendar, 
    color: 'text-orange-400', 
    bg: 'bg-orange-400/10', 
    border: 'border-orange-400/30',
    description: 'Tiene visita programada'
  },
  'instalado': { 
    label: 'Instalado', 
    icon: Home, 
    color: 'text-purple-400', 
    bg: 'bg-purple-400/10', 
    border: 'border-purple-400/30',
    description: 'Servicio instalado'
  },
  'activo': { 
    label: 'Cliente activo', 
    icon: Clock, 
    color: 'text-cyan-400', 
    bg: 'bg-cyan-400/10', 
    border: 'border-cyan-400/30',
    description: 'Pagando mensualmente'
  },
  'validado': { 
    label: 'Validado', 
    icon: Award, 
    color: 'text-green-400', 
    bg: 'bg-green-400/10', 
    border: 'border-green-400/30',
    description: '¡Recompensa desbloqueada!'
  },
  'recompensa_entregada': { 
    label: 'Completado', 
    icon: Gift, 
    color: 'text-white', 
    bg: 'bg-white/10', 
    border: 'border-white/30',
    description: 'Recompensa entregada'
  }
};

// Progress Bar Component
function PaymentProgress({ payments }) {
  return (
    <div className="flex gap-1 w-full" data-testid="payment-progress">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
            index < payments ? 'bg-white shadow-[0_0_6px_rgba(255,255,255,0.5)]' : 'bg-white/10'
          }`}
        />
      ))}
    </div>
  );
}

// Enhanced Status Badge Component
function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['registrado'];
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg} ${config.border} border`}>
      <Icon className={`w-3.5 h-3.5 ${config.color}`} />
      <span className={`text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    </div>
  );
}

// Enhanced Referral Card Component
function ReferralCard({ referral }) {
  const isValidated = referral.status === 'validado';
  const config = STATUS_CONFIG[referral.status] || STATUS_CONFIG['registrado'];
  
  // Format registration date
  const registrationDate = referral.created_at 
    ? new Date(referral.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  
  return (
    <div 
      className={`p-5 rounded-xl transition-all duration-300 ${
        isValidated 
          ? 'bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-white/30 shadow-[0_0_30px_rgba(255,255,255,0.1)]' 
          : 'bg-[#0A0A0A] border border-white/10 hover:border-white/20 hover:bg-[#0f0f0f]'
      }`}
      data-testid={`referral-card-${referral.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center`}>
            <span className="text-lg">{referral.name.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <h4 className="text-white font-medium">{referral.name}</h4>
            <p className="text-xs text-[#52525B]">{config.description}</p>
          </div>
        </div>
        {isValidated && (
          <span className="text-2xl animate-bounce" title="Recompensa desbloqueada">
            🎁
          </span>
        )}
      </div>
      
      <div className="flex items-center justify-between mb-4">
        <StatusBadge status={referral.status} />
        {registrationDate && (
          <span className="text-xs text-zinc-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {registrationDate}
          </span>
        )}
      </div>
      
      {/* Payment progress only for active/paying status */}
      {(referral.status === 'activo' || referral.status === 'validado' || referral.payments_made > 0) && (
        <div className="space-y-2 p-3 rounded-lg bg-white/5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#A1A1AA]">Meses pagados</span>
            <span className="text-white font-mono font-medium">{referral.payments_made}/3</span>
          </div>
          <PaymentProgress payments={referral.payments_made} />
          {referral.payments_made < 3 && (
            <p className="text-xs text-[#52525B]">
              Faltan {3 - referral.payments_made} {3 - referral.payments_made === 1 ? 'mes' : 'meses'} para validar
            </p>
          )}
        </div>
      )}
      
      {isValidated && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-sm text-[#25D366] flex items-center gap-2 font-medium">
            <Gift className="w-4 h-4" />
            +1 mes gratis para ti
          </p>
        </div>
      )}
    </div>
  );
}

// WhatsApp CTA Button Component
function WhatsAppButton({ code, size = 'default', className = '' }) {
  const appUrl = window.location.origin;
  const referralLink = `${appUrl}/ref/${code}`;
  const whatsappMessage = `Hola. Yo ya disfruto del agua de Manklar. Descarga la app y utiliza mi código ${code} para obtener tus beneficios.\n\n${referralLink}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;

  const sizeClasses = {
    default: 'h-14 px-8 text-base',
    large: 'h-16 px-10 text-lg',
    small: 'h-12 px-6 text-sm'
  };

  return (
    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className={className}>
      <Button 
        className={`w-full ${sizeClasses[size]} bg-[#25D366] text-black hover:bg-[#20bd5a] rounded-xl font-bold transition-all shadow-[0_0_20px_rgba(37,211,102,0.3)] hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] flex items-center justify-center gap-3`}
        data-testid="whatsapp-cta-btn"
      >
        <MessageCircle className="w-6 h-6" />
        Compartir por WhatsApp
      </Button>
    </a>
  );
}

// Volume Reward Progress Component
function VolumeRewardCard({ validatedReferrals, referralCode }) {
  const GOAL = 10;
  const REWARD_MONTHS = 6;
  const progress = Math.min(validatedReferrals, GOAL);
  const remaining = GOAL - progress;
  const isCompleted = progress >= GOAL;
  const progressPercent = (progress / GOAL) * 100;

  return (
    <div 
      className={`p-6 md:p-8 rounded-2xl mb-8 relative overflow-hidden transition-all duration-500 ${
        isCompleted 
          ? 'bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-white/30 shadow-[0_0_40px_rgba(255,255,255,0.15)]' 
          : 'bg-gradient-to-br from-[#0f0f0f] to-[#0a0a0a] border border-white/10'
      }`}
      data-testid="volume-reward-card"
    >
      {isCompleted && (
        <div className="absolute top-4 right-4">
          <Sparkles className="w-8 h-8 text-white/50 animate-pulse" />
        </div>
      )}

      {isCompleted ? (
        <div className="text-center py-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center mb-6 animate-pulse">
            <Trophy className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-2xl font-medium text-white font-['Outfit'] mb-2">
            🎉 ¡FELICIDADES!
          </h3>
          <p className="text-xl text-white mb-4">
            Has ganado <span className="font-bold">{REWARD_MONTHS} meses GRATIS</span>
          </p>
          <p className="text-[#A1A1AA] text-sm">
            Contacta con nosotros para reclamar tu recompensa
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
                  <Target className="w-4 h-4 text-white" />
                  <span className="text-sm text-white font-medium">Meta: {GOAL} referidos</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
                  <Trophy className="w-4 h-4 text-white" />
                  <span className="text-sm text-white font-medium">Premio: {REWARD_MONTHS} meses GRATIS</span>
                </div>
              </div>

              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-5xl font-light text-white font-['Outfit']">{progress}</span>
                <span className="text-2xl text-[#52525B] font-light">/ {GOAL}</span>
                <span className="text-[#A1A1AA]">validados</span>
              </div>

              <div className="mb-4">
                <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-white to-white/80 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              <p className="text-lg text-white">
                Te {remaining === 1 ? 'falta' : 'faltan'} <span className="font-bold text-xl">{remaining}</span> {remaining === 1 ? 'referido' : 'referidos'} para {REWARD_MONTHS} meses gratis
              </p>
            </div>

            <div className="lg:w-72 flex flex-col gap-4">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
                <p className="text-4xl font-bold text-white font-['Outfit'] mb-1">
                  {remaining}
                </p>
                <p className="text-sm text-[#A1A1AA]">
                  {remaining === 1 ? 'restante' : 'restantes'}
                </p>
              </div>
              <WhatsAppButton code={referralCode} size="default" />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex gap-1">
              {Array.from({ length: GOAL }).map((_, index) => (
                <div
                  key={`progress-step-${index}`}
                  className={`flex-1 h-3 rounded-full transition-all duration-300 ${
                    index < progress 
                      ? 'bg-white shadow-[0_0_8px_rgba(255,255,255,0.3)]' 
                      : 'bg-white/10'
                  }`}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Stats Summary Cards
function StatsSummary({ user, referrals }) {
  const pendingCount = referrals.filter(r => !['validado', 'recompensa_entregada'].includes(r.status)).length;
  const convertedCount = referrals.filter(r => ['validado', 'recompensa_entregada'].includes(r.status)).length;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <div className="p-5 rounded-xl bg-[#0A0A0A] border border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-[#52525B]" />
          <span className="text-xs text-[#52525B]">Total</span>
        </div>
        <p className="text-3xl font-light text-white font-['Outfit']">
          {user?.total_referrals || 0}
        </p>
        <p className="text-xs text-[#52525B] mt-1">recomendaciones</p>
      </div>
      
      <div className="p-5 rounded-xl bg-[#0A0A0A] border border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-yellow-400" />
          <span className="text-xs text-[#52525B]">Pendientes</span>
        </div>
        <p className="text-3xl font-light text-white font-['Outfit']">
          {pendingCount}
        </p>
        <p className="text-xs text-[#52525B] mt-1">en proceso</p>
      </div>
      
      <div className="p-5 rounded-xl bg-[#0A0A0A] border border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <UserCheck className="w-4 h-4 text-green-400" />
          <span className="text-xs text-[#52525B]">Convertidos</span>
        </div>
        <p className="text-3xl font-light text-white font-['Outfit']">
          {convertedCount}
        </p>
        <p className="text-xs text-[#52525B] mt-1">clientes activos</p>
      </div>
      
      <div className={`p-5 rounded-xl transition-all ${
        user?.available_rewards > 0 
          ? 'bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.1)]' 
          : 'bg-[#0A0A0A] border border-white/10'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <Gift className={`w-4 h-4 ${user?.available_rewards > 0 ? 'text-white' : 'text-[#52525B]'}`} />
          <span className="text-xs text-[#52525B]">Recompensas</span>
        </div>
        <p className={`text-3xl font-light font-['Outfit'] ${user?.available_rewards > 0 ? 'text-white' : 'text-[#A1A1AA]'}`}>
          {user?.available_rewards || 0}
        </p>
        <p className="text-xs text-[#52525B] mt-1">meses gratis</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const validatedReferrals = user?.validated_referrals || 0;
  const GOAL = 10;
  const remaining = Math.max(0, GOAL - validatedReferrals);

  const appUrl = window.location.origin;
  const referralLink = `${appUrl}/ref/${user?.referral_code}`;

  const fetchReferrals = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/referrals`, {
        withCredentials: true,
      });
      setReferrals(response.data);
    } catch (error) {
      // Silent fail - user will see empty state
      console.debug('Error fetching referrals:', error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#050505' }}>
        <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 bg-[#030303]/90 backdrop-blur-xl z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-['Manrope']">Inicio</span>
            </Link>
            <img 
              src="/manklar-logo-white.png" 
              alt="Manklar" 
              className="h-9 drop-shadow-[0_0_20px_rgba(255,255,255,0.1)]"
            />
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            {isAdmin && (
              <Link to="/admin">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-lg px-2 sm:px-3"
                  data-testid="admin-panel-link"
                >
                  <Shield className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              </Link>
            )}
            <Link to="/help">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-lg px-2 sm:px-3"
                data-testid="help-center-link"
              >
                <HelpCircle className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Ayuda</span>
              </Button>
            </Link>
            <Button 
              variant="ghost"
              size="sm" 
              onClick={handleLogout}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-lg px-2 sm:px-3"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 md:py-12">
        {/* Hero Section */}
        <div className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-white font-['Outfit'] mb-3">
            Hola, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p className="text-xl text-[#A1A1AA]">
            {remaining > 0 ? (
              <>Estás a <span className="text-white font-semibold">{remaining}</span> referidos de conseguir <span className="text-white font-semibold">6 meses GRATIS</span></>
            ) : (
              <span className="text-[#25D366]">🎉 ¡Ya conseguiste tus 6 meses gratis!</span>
            )}
          </p>
        </div>

        {/* Share Section */}
        <div className="p-6 rounded-2xl mb-8 bg-gradient-to-br from-[#0f0f0f] to-[#0a0a0a] border border-white/10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex-1">
              <h2 className="text-lg font-medium text-white mb-4 font-['Outfit']">
                Tu código personal
              </h2>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-3xl font-mono text-white tracking-widest" data-testid="referral-code">
                  {user?.referral_code}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyCode}
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-lg"
                  data-testid="copy-code-btn"
                >
                  {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copied ? '¡Copiado!' : 'Copiar código'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyLink}
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-lg"
                  data-testid="copy-link-btn"
                >
                  {linkCopied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {linkCopied ? '¡Copiado!' : 'Copiar enlace'}
                </Button>
              </div>
            </div>
            
            <WhatsAppButton code={user?.referral_code} size="large" className="lg:w-auto" />
          </div>
        </div>

        {/* Volume Reward Promotion */}
        <VolumeRewardCard 
          validatedReferrals={validatedReferrals} 
          referralCode={user?.referral_code}
        />

        {/* How It Works Section */}
        <div className="mb-8 p-4 sm:p-6 lg:p-8 rounded-2xl bg-gradient-to-br from-[#0a0a0a] to-[#050505] border border-white/10">
          <h2 className="text-xl sm:text-2xl font-medium text-white font-['Outfit'] mb-6 text-center">
            ¿Cómo funciona el programa de referidos?
          </h2>
          
          {/* Steps */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {/* Step 1 */}
            <div className="flex flex-col items-center text-center p-4 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-3">
                <span className="text-lg">1️⃣</span>
              </div>
              <p className="text-sm text-zinc-300">
                <span className="text-white font-medium">Comparte</span> tu código o enlace personal
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="flex flex-col items-center text-center p-4 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-3">
                <span className="text-lg">2️⃣</span>
              </div>
              <p className="text-sm text-zinc-300">
                Tu amigo se convierte en <span className="text-white font-medium">cliente Manklar</span>
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="flex flex-col items-center text-center p-4 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-3">
                <span className="text-lg">3️⃣</span>
              </div>
              <p className="text-sm text-zinc-300">
                Cuando complete <span className="text-white font-medium">3 meses de pago</span>, pasa a <span className="text-emerald-400 font-medium">VALIDADO</span>
              </p>
            </div>
            
            {/* Step 4 */}
            <div className="flex flex-col items-center text-center p-4 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-3">
                <span className="text-lg">4️⃣</span>
              </div>
              <p className="text-sm text-zinc-300">
                Por cada referido validado recibes <span className="text-white font-medium">1 mes GRATIS</span>
              </p>
            </div>
            
            {/* Step 5 */}
            <div className="flex flex-col items-center text-center p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
                <span className="text-lg">🎁</span>
              </div>
              <p className="text-sm text-zinc-300">
                Con <span className="text-amber-400 font-medium">10 validados</span> obtienes <span className="text-amber-400 font-medium">+6 meses GRATIS</span>
              </p>
            </div>
          </div>
          
          {/* Progress Summary */}
          <div className="p-4 sm:p-6 rounded-xl bg-white/[0.03] border border-white/10">
            <h3 className="text-sm uppercase tracking-[0.15em] text-zinc-500 mb-4 text-center">
              Tu Progreso
            </h3>
            
            {/* Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
              <div className="p-3 rounded-lg bg-white/5 text-center">
                <p className="text-2xl sm:text-3xl font-light text-white font-['Outfit']">
                  {referrals.length}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-500 mt-1">Registrados</p>
              </div>
              <div className="p-3 rounded-lg bg-white/5 text-center">
                <p className="text-2xl sm:text-3xl font-light text-cyan-400 font-['Outfit']">
                  {referrals.filter(r => r.status === 'activo').length}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-500 mt-1">Activos</p>
              </div>
              <div className="p-3 rounded-lg bg-white/5 text-center">
                <p className="text-2xl sm:text-3xl font-light text-emerald-400 font-['Outfit']">
                  {validatedReferrals}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-500 mt-1">Validados</p>
              </div>
              <div className="p-3 rounded-lg bg-gradient-to-br from-white/10 to-white/5 text-center border border-white/10">
                <p className="text-2xl sm:text-3xl font-light text-white font-['Outfit']">
                  {validatedReferrals >= 10 ? validatedReferrals + 6 : validatedReferrals}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-400 mt-1">Meses gratis</p>
              </div>
            </div>
            
            {/* Progress Bar to 10 */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-zinc-400">Progreso hacia bonificación</span>
                <span className="text-white font-mono">{Math.min(validatedReferrals, 10)}/10</span>
              </div>
              <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    validatedReferrals >= 10 
                      ? 'bg-gradient-to-r from-amber-500 to-amber-400' 
                      : 'bg-gradient-to-r from-white/40 to-white/60'
                  }`}
                  style={{ width: `${Math.min((validatedReferrals / 10) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-center text-zinc-400 mt-2">
                {validatedReferrals >= 10 ? (
                  <span className="text-amber-400">🎉 ¡Bonificación desbloqueada! +6 meses gratis adicionales</span>
                ) : (
                  <>Te faltan <span className="text-white font-medium">{10 - validatedReferrals} referidos validados</span> para desbloquear <span className="text-amber-400 font-medium">6 meses gratis adicionales</span></>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <StatsSummary user={user} referrals={referrals} />

        {/* Referrals List */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-medium text-white font-['Outfit']">
              Mis Recomendaciones
            </h2>
            <span className="text-sm text-[#52525B]">
              {referrals.length} {referrals.length === 1 ? 'recomendación' : 'recomendaciones'}
            </span>
          </div>

          {referrals.length === 0 ? (
            <div className="p-10 rounded-2xl text-center bg-gradient-to-b from-[#0f0f0f] to-[#0a0a0a] border border-white/10">
              <div className="w-20 h-20 mx-auto rounded-full bg-white/5 flex items-center justify-center mb-6">
                <Users className="w-10 h-10 text-[#52525B]" />
              </div>
              <h3 className="text-2xl text-white mb-2 font-['Outfit']">¡Empieza a recomendar!</h3>
              <p className="text-[#A1A1AA] mb-2 max-w-md mx-auto">
                Comparte tu código con amigos y familia. Por cada persona que se convierta en cliente, ganarás un mes gratis.
              </p>
              <p className="text-white text-lg mb-6">
                🎁 Tu primer referido validado = <span className="font-bold">1 mes gratis</span>
              </p>
              <WhatsAppButton code={user?.referral_code} size="large" className="max-w-sm mx-auto" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="referrals-list">
                {referrals.map((referral) => (
                  <ReferralCard key={referral.id} referral={referral} />
                ))}
              </div>
              
              {/* CTA after referrals list */}
              <div className="p-6 rounded-2xl bg-gradient-to-b from-[#0f0f0f] to-[#0a0a0a] border border-white/10 text-center">
                <p className="text-white mb-4 text-lg">
                  🚀 ¿Conoces a más personas interesadas en el agua de Manklar?
                </p>
                <WhatsAppButton code={user?.referral_code} size="default" className="max-w-md mx-auto" />
              </div>
            </>
          )}
        </div>
      </main>

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
}
